'use client';

import React from 'react';
import Link from 'next/link';
import { Shield, ArrowLeft } from 'lucide-react';

export default function DataRetentionPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link
            href="/"
            className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors text-sm font-medium"
          >
            <ArrowLeft size={16} />
            Back to Ertzyx
          </Link>
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-blue-700" />
            <span className="font-bold text-slate-800">Ertzyx</span>
          </div>
        </div>
      </div>
      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="mb-10">
          <h1 className="text-4xl font-bold text-slate-900 mb-3">Data Retention Policy</h1>
          <p className="text-slate-500 text-sm">
            Last updated: 25 March 2026 · Effective: 25 March 2026
          </p>
          <p className="text-slate-600 mt-4 leading-relaxed">
            This Data Retention Policy describes how long Ertzyx retains different categories of
            personal data, and the procedures for deletion and anonymisation. This policy applies to
            all data processed by Ertzyx at{' '}
            <a href="https://ledger.ertzyx.com" className="text-blue-700 underline">
              ledger.ertzyx.com
            </a>
            .
          </p>
        </div>

        <div className="space-y-10 text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Principles</h2>
            <p className="mb-3">
              We retain personal data only for as long as necessary to fulfil the purposes for which
              it was collected, including:
            </p>
            <ul className="space-y-2 list-disc list-inside text-slate-600">
              <li>Providing the services you have requested</li>
              <li>Complying with legal, regulatory, and contractual obligations</li>
              <li>Resolving disputes and enforcing our agreements</li>
              <li>Maintaining the integrity and permanence of verified achievement records</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Retention Schedule</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-slate-200 rounded-xl overflow-hidden">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700">
                      Data Category
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700">
                      Retention Period
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700">Basis</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[
                    [
                      'Account information (name, email)',
                      'Duration of account + 30 days after deletion',
                      'Contract performance',
                    ],
                    ['Achievement records (active)', 'Duration of account', 'Contract performance'],
                    [
                      'Verified achievement records',
                      'Duration of account + 7 years after deletion',
                      'Legitimate interests (record integrity)',
                    ],
                    [
                      'Uploaded media files',
                      'Duration of account + 30 days after deletion',
                      'Contract performance',
                    ],
                    [
                      'Verification history',
                      'Duration of account + 7 years',
                      'Legitimate interests / Legal obligation',
                    ],
                    [
                      'Payment records',
                      '7 years from transaction date',
                      'Legal obligation (tax/accounting)',
                    ],
                    ['Contact form submissions', '2 years from submission', 'Legitimate interests'],
                    ['Session and usage logs', '90 days', 'Security / Legitimate interests'],
                    ['Authentication logs', '1 year', 'Security / Legal obligation'],
                    ['Interest and contact submissions', '1 year from submission', 'Consent'],
                    ['Cookie consent records', '2 years', 'Legal obligation (GDPR)'],
                    [
                      'Anonymised analytics data',
                      'Indefinite (no personal data)',
                      'Legitimate interests',
                    ],
                  ]?.map(([category, period, basis]) => (
                    <tr key={category} className="bg-white">
                      <td className="px-4 py-3 text-slate-700 font-medium">{category}</td>
                      <td className="px-4 py-3 text-slate-600">{period}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{basis}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              3. Verified Records — Special Consideration
            </h2>
            <p className="mb-3">
              Ertzyx&apos;s core purpose is the permanent preservation of verified achievement
              records. Because of this:
            </p>
            <ul className="space-y-2 list-disc list-inside text-slate-600">
              <li>
                Verified records that have been made public and shared via credential links may
                remain accessible for a transition period after account deletion to protect the
                interests of third parties who have relied on those records
              </li>
              <li>
                The verification metadata (institution name, date, verification reference) may be
                retained in anonymised form to maintain the integrity of the verification chain
              </li>
              <li>You will be informed of this at the time of account deletion</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Account Deletion Process</h2>
            <p className="mb-3">When you delete your account:</p>
            <ol className="space-y-3 list-decimal list-inside text-slate-600">
              <li>
                <strong>Immediate:</strong> Your account is deactivated and you lose access to the
                Platform
              </li>
              <li>
                <strong>Within 30 days:</strong> Your personal profile data, unverified
                achievements, and uploaded media are permanently deleted from our active systems
              </li>
              <li>
                <strong>Within 90 days:</strong> Your data is purged from all backup systems
              </li>
              <li>
                <strong>Retained:</strong> Payment records (7 years), verified achievement metadata
                (7 years, anonymised where possible), and any data required for legal compliance
              </li>
            </ol>
            <p className="mt-4">
              To request account deletion: Settings → Account → Delete Account, or email{' '}
              <a href="mailto:support@ertzyx.com" className="text-blue-700 underline">
                support@ertzyx.com
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              5. Data Stored by Third-Party Processors
            </h2>
            <p className="mb-3 text-slate-600">
              Certain trusted third-party service providers that we use to operate the platform may
              retain data in accordance with their own policies and legal obligations. We require
              these providers to handle data only for authorised purposes.
            </p>
            <div className="space-y-3">
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-semibold text-slate-800 mb-1">
                  Database and File Storage Providers
                </h3>
                <p className="text-sm text-slate-600">
                  Data is retained on our cloud infrastructure providers&apos; systems in
                  accordance with our deletion requests and applicable data processing agreements.
                  Infrastructure providers may retain system backups for a limited period as part
                  of standard operations.
                </p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-semibold text-slate-800 mb-1">Payment Processing Providers</h3>
                <p className="text-sm text-slate-600">
                  If paid services are offered, our payment processing provider may retain
                  transaction records for up to 7 years for financial compliance purposes, in
                  accordance with applicable legal and regulatory requirements.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Your Rights</h2>
            <p>
              You have the right to request deletion of your personal data at any time, subject to
              the exceptions described in this policy. You also have the right to request a copy of
              all data we hold about you. To exercise these rights, contact{' '}
              <a href="mailto:support@ertzyx.com" className="text-blue-700 underline">
                support@ertzyx.com
              </a>
              . We will respond within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Changes to This Policy</h2>
            <p>
              We may update this Data Retention Policy from time to time. Changes will be
              communicated via email or a notice on the Platform. Continued use of Ertzyx after
              changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Contact</h2>
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <p className="font-semibold text-slate-800">Ertzyx Data Team</p>
              <p className="text-slate-600 text-sm mt-1">
                Email:{' '}
                <a href="mailto:support@ertzyx.com" className="text-blue-700 underline">
                  support@ertzyx.com
                </a>
              </p>
              <p className="text-slate-600 text-sm">
                Platform:{' '}
                <a href="https://ledger.ertzyx.com" className="text-blue-700 underline">
                  ledger.ertzyx.com
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>
      {/* Footer nav */}
      <div className="border-t border-slate-200 bg-white py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-wrap gap-4 text-sm text-slate-500">
          <Link href="/privacy-policy" className="hover:text-slate-800 transition-colors">
            Privacy Policy
          </Link>
          <Link href="/terms-of-service" className="hover:text-slate-800 transition-colors">
            Terms of Service
          </Link>
          <Link href="/cookie-policy" className="hover:text-slate-800 transition-colors">
            Cookie Policy
          </Link>
          <Link href="/contact" className="hover:text-slate-800 transition-colors">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
}
